import * as React from 'react';

import { AnyCalendarDate } from '@internationalized/date';
import {
  SalesOrderStatus,
  enumSalesOrderStatus,
} from '@rematter/frontend-lib-api-client';
import { DataGridColumn, LocalizedDate, Button, Menu } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucideMoreHorizontal } from 'lucide-react';

import { SalesOrderRecord } from '../../../types/salesOrder';
import { CreatedByFilter } from '../../_shared/filters/CreatedByFilter';
import { SalesOrderStatusBadge } from '../../_shared/StatusBadges';
import { CreatedOnDateRangeColumnFilter } from '../filters/CreatedOnDateRangeColumnFilter';
import { DateRangeColumnFilter } from '../filters/DateRangeColumnFilter';
import { StatusColumnFilter } from '../filters/StatusColumnFilter';
import { NetWeightCell } from '../NetWeightCell';

import { useSalesOrderDetailMenuItems } from './useSalesOrderDetailMenuItems';

import { CustomerFilter } from '~/modules/trade/_components/_shared/CustomerFilter';
import { FacilityRecord } from '~/modules/trade/_graphql/queryFacilities';
import * as state from '~/modules/trade/_state/salesOrdersList.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

const columns = [
  'documentNumber',
  'startDate',
  'endDate',
  'counterpartyReferenceNumber',
  'customer',
  'lineItemCount',
  'netWeight',
  'shippedLoadsCount',
  'fulfilledPercentage',
  'status',
  'totalPrice',
  'createdOn',
  'createdBy',
];

const STATUS_COLUMN_KEYS: Record<SalesOrderStatus, string[]> = {
  Draft: columns,
  Open: columns,
  Finished: columns,
  Voided: columns,
};

export type CommonCellProps = {
  className: string;
  onClick?: () => void;
  onDoubleClick?: () => void;
};

type Args = {
  status?: SalesOrderStatus;
  facilities?: FacilityRecord[];
  commonCellProps?: (salesOrder: SalesOrderRecord) => CommonCellProps;
  setIsLoadDialogOpen?: (open: boolean, salesOrderId?: string) => void;
  setSelectedSalesOrder?: (salesOrder: SalesOrderRecord | null) => void;
  onVoid?: (salesOrderId: string) => void;
  onClose?: (salesOrderId: string) => void;
  onRefetch?: () => void;
  customerFilter?:
    | React.ReactNode
    | ((values: { close: () => void }) => React.ReactNode);
  isCustomerFilterActive?: boolean;
};

// Component for the action menu cell
export type SalesOrderActionMenuProps = {
  salesOrder: SalesOrderRecord;
  setIsLoadDialogOpen?: (open: boolean, salesOrderId?: string) => void;
  setSelectedSalesOrder?: (salesOrder: SalesOrderRecord | null) => void;
  onVoid?: (salesOrderId: string) => void;
  onClose?: (salesOrderId: string) => void;
  onRefetch?: () => void;
};

const SalesOrderActionMenu = ({
  salesOrder,
  setIsLoadDialogOpen,
  setSelectedSalesOrder,
  onVoid,
  onRefetch,
  onClose,
}: SalesOrderActionMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const menuItems = useSalesOrderDetailMenuItems({
    salesOrder,
    showVoidButton:
      salesOrder.status === enumSalesOrderStatus.Open ||
      salesOrder.status === enumSalesOrderStatus.Draft,
    setIsLoadDialogOpen: (open: boolean, salesOrderId?: string) => {
      if (open && setSelectedSalesOrder) {
        setSelectedSalesOrder(salesOrder);
      }
      setIsLoadDialogOpen?.(open, salesOrderId);
      setIsMenuOpen(open);
    },
    onClose,
    onVoid: onVoid,
    onRefetch: onRefetch,
    displayListActions: true,
    isMenuOpen,
  });

  return (
    <div className="@SalesOrderActionMenu flex justify-center">
      <Menu
        trigger={
          <Button
            size="icon"
            icon={<LucideMoreHorizontal />}
            variant="outline"
          />
        }
        items={menuItems}
        onOpenChange={setIsMenuOpen}
      />
    </div>
  );
};

export const useSalesOrdersListColumns = ({
  status,
  facilities = [],
  commonCellProps,
  setIsLoadDialogOpen,
  setSelectedSalesOrder,
  onVoid,
  onClose,
  onRefetch,
}: Args = {}) => {
  const t = useTranslations();

  const setOffset = useSetAtom(state.$offset);

  const [selectedCustomerIds, setSelectedCustomerIds] = useAtom(
    state.$selectedCustomerIds,
  );

  const [selectedCreatedByUserIds, setSelectedCreatedByUserIds] = useAtom(
    state.$selectedCreatedByUserIds,
  );

  const selectedStatusesSet = useAtomValue(state.$columnSelectedStatuses);
  const columnDateRange = useAtomValue(state.$columnDateRange);
  const columnCreatedOnDateRange = useAtomValue(
    state.$columnCreatedOnDateRange,
  );

  // Customer filter handlers
  const handleCustomerSelectionChange = React.useCallback(
    (customerIds: Set<React.Key>) => {
      setSelectedCustomerIds(customerIds as Set<string>);
    },
    [setSelectedCustomerIds],
  );

  const handleCustomerFilterReset = React.useCallback(() => {
    setSelectedCustomerIds(new Set());
    setOffset(0);
  }, [setSelectedCustomerIds, setOffset]);

  const ALL_COLUMNS: Record<
    string,
    DataGridColumn<SalesOrderRecord>
  > = React.useMemo(
    () => ({
      endDate: {
        key: 'endDate',
        header: t.salesOrdersView.table.columns.endDate,
        sortable: true,
        columnFilter: ({ close }) => <DateRangeColumnFilter onClose={close} />,
        isFilterActive: !!columnDateRange,
        cell: ({ row }) => {
          const content = row.endDate ? (
            <LocalizedDate date={row.endDate as AnyCalendarDate} />
          ) : (
            '-'
          );
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      startDate: {
        key: 'startDate',
        header: t.salesOrdersView.table.columns.startDate,
        sortable: true,
        columnFilter: ({ close }) => <DateRangeColumnFilter onClose={close} />,
        isFilterActive: !!columnDateRange,
        cell: ({ row }) => {
          const content = row.startDate ? (
            <LocalizedDate date={row.startDate as AnyCalendarDate} />
          ) : (
            '-'
          );
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      documentNumber: {
        key: 'documentNumber',
        header: t.salesOrdersView.table.columns.salesOrderNumber,
        sortable: true,
        cell: ({ row }) => {
          const content = row.documentNumber ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      counterpartyReferenceNumber: {
        key: 'counterpartyReferenceNumber',
        header: t.salesOrdersView.table.columns.counterpartyPurchaseOrderNumber,
        cell: ({ row }) => {
          const content = row.counterpartyReferenceNumber ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      customer: {
        key: 'customer',
        header: t.salesOrdersView.table.columns.customer,
        columnFilter: ({ close }) => (
          <CustomerFilter
            selectedCustomerIds={selectedCustomerIds}
            onSelectionChange={handleCustomerSelectionChange}
            onReset={handleCustomerFilterReset}
            onClose={close}
          />
        ),
        isFilterActive: selectedCustomerIds.size > 0,
        cell: ({ row }) => {
          const content = row.customerName ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      facilityName: {
        key: 'facilityName',
        header: t.salesOrdersView.table.columns.facility,
        cell: ({ row }) => {
          const facility = row.facility?.id
            ? facilities.find((f) => f.id === row.facility.id)
            : null;
          const content = facility?.facilityName ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      lineItemCount: {
        key: 'lineItemCount',
        header: t.salesOrdersView.table.columns.numberOfMaterials,
        cell: ({ row }) => {
          const content = row.lineItemCount ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-right"
            >
              {content}
            </div>
          ) : (
            <div className="text-right">{content}</div>
          );
        },
      },
      netWeight: {
        key: 'netWeight',
        header: t.salesOrdersView.table.columns.netWeight,
        cell: ({ row }) => (
          <NetWeightCell row={row} commonCellProps={commonCellProps} />
        ),
      },
      shippedLoadsCount: {
        key: 'shippedLoadsCount',
        header: t.salesOrdersView.table.columns.shippedLoadsNumber,
        cell: ({ row }) => {
          const content = row.shippedLoadsCount ?? '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-right"
            >
              {content}
            </div>
          ) : (
            <div className="text-right">{content}</div>
          );
        },
      },
      fulfilledPercentage: {
        key: 'fulfilledPercentage',
        header: t.salesOrdersView.table.columns.fulfilledPercentage,
        cell: ({ row }) => {
          const percentage = row.fulfilledPercentage;
          const content = percentage !== undefined ? `${percentage}%` : '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-right"
            >
              {content}
            </div>
          ) : (
            <div className="text-right">{content}</div>
          );
        },
      },
      status: {
        key: 'status',
        header: t.salesOrdersView.table.columns.status,
        sortable: true,
        columnFilter: ({ close }) => <StatusColumnFilter onClose={close} />,
        isFilterActive: selectedStatusesSet.size > 0,
        cell: ({ row }) => {
          const content = !row.status ? (
            '-'
          ) : (
            <SalesOrderStatusBadge status={row.status} />
          );
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      totalPrice: {
        key: 'totalPrice',
        header: t.salesOrdersView.table.columns.total,
        cell: ({ row }) => {
          const total = row.totalPrice?.amount;
          const content = total ? `$${total.toLocaleString()}` : '-';
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-right"
            >
              {content}
            </div>
          ) : (
            <div className="text-right">{content}</div>
          );
        },
      },
      createdOn: {
        key: 'createdOn',
        header: t.salesOrdersView.table.columns.createdOn,
        sortable: true,
        columnFilter: ({ close }) => (
          <CreatedOnDateRangeColumnFilter onClose={close} />
        ),
        isFilterActive: !!columnCreatedOnDateRange,
        cell: ({ row }) => {
          const content = row.timestamps?.createdAt ? (
            <LocalizedDate date={row.timestamps.createdAt as AnyCalendarDate} />
          ) : (
            '-'
          );
          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {content}
            </div>
          ) : (
            <div className="text-left">{content}</div>
          );
        },
      },
      createdBy: {
        key: 'createdBy',
        header: t.salesOrdersView.table.columns.createdBy,
        columnFilter: ({ close }) => (
          <CreatedByFilter
            selectedUserIds={selectedCreatedByUserIds}
            onSelectionChange={(userIds) => {
              setSelectedCreatedByUserIds(
                new Set(Array.from(userIds, (key) => String(key))),
              );
              setOffset(0);
            }}
            onReset={() => {
              setSelectedCreatedByUserIds(new Set());
              setOffset(0);
            }}
            onClose={close}
          />
        ),
        isFilterActive: selectedCreatedByUserIds.size > 0,
        cell: ({ row }) => {
          const createdByUser = row.createdByUser;
          const displayName = createdByUser
            ? `${createdByUser.firstName} ${createdByUser.lastName}`
            : 'System';

          return commonCellProps ? (
            <div
              {...commonCellProps(row as SalesOrderRecord)}
              className="text-left"
            >
              {displayName}
            </div>
          ) : (
            <div className="text-left">{displayName}</div>
          );
        },
      },
      actions: {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => {
          const salesOrder = row as SalesOrderRecord;
          return (
            <SalesOrderActionMenu
              salesOrder={salesOrder}
              setIsLoadDialogOpen={(open: boolean) => {
                setIsLoadDialogOpen?.(open, salesOrder.id);
              }}
              setSelectedSalesOrder={setSelectedSalesOrder}
              onVoid={onVoid}
              onClose={onClose}
              onRefetch={onRefetch}
            />
          );
        },
      },
    }),
    [
      columnCreatedOnDateRange,
      columnDateRange,
      commonCellProps,
      facilities,
      handleCustomerFilterReset,
      handleCustomerSelectionChange,
      onClose,
      onRefetch,
      onVoid,
      selectedCreatedByUserIds,
      selectedCustomerIds,
      selectedStatusesSet.size,
      setIsLoadDialogOpen,
      setOffset,
      setSelectedCreatedByUserIds,
      setSelectedSalesOrder,
      t.salesOrdersView.table.columns.endDate,
      t.salesOrdersView.table.columns.counterpartyPurchaseOrderNumber,
      t.salesOrdersView.table.columns.createdBy,
      t.salesOrdersView.table.columns.createdOn,
      t.salesOrdersView.table.columns.customer,
      t.salesOrdersView.table.columns.facility,
      t.salesOrdersView.table.columns.fulfilledPercentage,
      t.salesOrdersView.table.columns.netWeight,
      t.salesOrdersView.table.columns.numberOfMaterials,
      t.salesOrdersView.table.columns.salesOrderNumber,
      t.salesOrdersView.table.columns.shippedLoadsNumber,
      t.salesOrdersView.table.columns.startDate,
      t.salesOrdersView.table.columns.status,
      t.salesOrdersView.table.columns.total,
    ],
  );

  const selectedStatus = status || enumSalesOrderStatus.Open;
  const visibleColumnKeys = STATUS_COLUMN_KEYS[selectedStatus];

  return React.useMemo(() => {
    const baseColumns = visibleColumnKeys
      .map((key) => ALL_COLUMNS[key])
      .filter(Boolean);
    // Add actions column at the end
    return [...baseColumns, ALL_COLUMNS.actions];
  }, [ALL_COLUMNS, visibleColumnKeys]);
};
